/**
 * Mobile Menu V2 Navigation Controller
 * Handles sliding navigation between menu levels
 */
class MobileMenuV2Navigation {
  private nav: HTMLElement;
  private currentLevel: number = 0;
  private levelHistory: number[] = [0];
  private transitioning: boolean = false;

  constructor(nav: HTMLElement) {
    this.nav = nav;
    this.init();
  }

  private init(): void {
    // Attach event listeners to all submenu triggers
    const submenuTriggers = this.nav.querySelectorAll<HTMLElement>('[data-action="open-submenu"]');
    submenuTriggers.forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        const listItem = trigger.closest<HTMLElement>('[data-item-id]');
        if (listItem) {
          // Prefer unique item ID if available, fallback to item ID
          const uniqueItemId = listItem.getAttribute('data-unique-item-id');
          const itemId = listItem.getAttribute('data-item-id');
          const finalId = uniqueItemId || itemId;
          
          if (finalId) {
            this.openSubmenu(finalId);
          }
        }
      });
    });

    // Attach event listeners to all back buttons
    const backButtons = this.nav.querySelectorAll<HTMLElement>('[data-action="back"]');
    backButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.goBack();
      });
    });
  }

  private openSubmenu(itemId: string): void {
    if (this.transitioning) return;

    // The itemId passed here is either a unique-item-id or a regular item-id
    // Try to find the corresponding level using the unique level-id
    let targetLevel = this.nav.querySelector<HTMLElement>(`.mobile-menu-level[data-level-id="${itemId}"]`);
    
    // Fallback: try with parent-id for backwards compatibility
    if (!targetLevel) {
      targetLevel = this.nav.querySelector<HTMLElement>(`.mobile-menu-level[data-parent-id="${itemId}"]`);
    }
    
    if (!targetLevel) return;

    const currentActiveLevel = this.nav.querySelector<HTMLElement>(`.mobile-menu-level[data-active="true"]`);
    if (!currentActiveLevel) return;

    this.transitioning = true;
    this.nav.setAttribute('data-transitioning', 'true');

    // Set direction for animation
    currentActiveLevel.setAttribute('data-direction', 'forward');
    targetLevel.setAttribute('data-direction', 'forward');

    // Mark current as leaving
    currentActiveLevel.setAttribute('data-active', 'leaving');

    // Show target level
    targetLevel.setAttribute('data-active', 'true');

    // Get level number and update history
    const levelNum = parseInt(targetLevel.getAttribute('data-level') || '0', 10);
    this.currentLevel = levelNum;
    this.levelHistory.push(levelNum);
    this.nav.setAttribute('data-current-level', String(levelNum));

    // Wait for animation to complete
    setTimeout(() => {
      currentActiveLevel.setAttribute('data-active', 'false');
      currentActiveLevel.removeAttribute('data-direction');
      targetLevel.removeAttribute('data-direction');
      this.transitioning = false;
      this.nav.setAttribute('data-transitioning', 'false');
    }, 300);
  }

  private goBack(): void {
    if (this.transitioning) return;
    if (this.levelHistory.length <= 1) return;

    const currentActiveLevel = this.nav.querySelector<HTMLElement>(`.mobile-menu-level[data-active="true"]`);
    if (!currentActiveLevel) return;

    // Remove current level from history
    this.levelHistory.pop();
    
    // Get previous level
    const previousLevelNum = this.levelHistory[this.levelHistory.length - 1];
    
    // Find the previous level element
    let previousLevel: HTMLElement | null = null;
    
    if (previousLevelNum === 0) {
      previousLevel = this.nav.querySelector<HTMLElement>(`.mobile-menu-level[data-level="0"]`);
    } else {
      // Use the level-id structure to find the parent
      const currentLevelId = currentActiveLevel.getAttribute('data-level-id');
      
      if (currentLevelId) {
        const currentLevel = parseInt(currentActiveLevel.getAttribute('data-level') || '0', 10);
        
        if (currentLevel === 2) {
          // Level 2: parse parent index from level-id
          const parts = currentLevelId.split('-');
          if (parts.length >= 2) {
            const parentIndex = parts[1];
            
            // Find the level 1 with matching parent index
            const allLevel1s = this.nav.querySelectorAll<HTMLElement>(`.mobile-menu-level[data-level="1"]`);
            for (const level of allLevel1s) {
              const levelId = level.getAttribute('data-level-id');
              if (levelId && levelId.endsWith(`-${parentIndex}`)) {
                previousLevel = level;
                break;
              }
            }
          }
        } else if (currentLevel === 1) {
          previousLevel = this.nav.querySelector<HTMLElement>(`.mobile-menu-level[data-level="0"]`);
        }
      }
    }

    if (!previousLevel) {
      // Fallback: go to level 0
      previousLevel = this.nav.querySelector<HTMLElement>(`.mobile-menu-level[data-level="0"]`);
    }

    if (!previousLevel) return;

    this.transitioning = true;
    this.nav.setAttribute('data-transitioning', 'true');

    // Set direction for backward animation
    currentActiveLevel.setAttribute('data-direction', 'backward');
    previousLevel.setAttribute('data-direction', 'backward');

    // Mark current as leaving
    currentActiveLevel.setAttribute('data-active', 'leaving');

    // Show previous level
    previousLevel.setAttribute('data-active', 'true');

    // Update current level
    this.currentLevel = previousLevelNum;
    this.nav.setAttribute('data-current-level', String(previousLevelNum));

    // Wait for animation to complete
    setTimeout(() => {
      currentActiveLevel.setAttribute('data-active', 'false');
      currentActiveLevel.removeAttribute('data-direction');
      previousLevel.removeAttribute('data-direction');
      this.transitioning = false;
      this.nav.setAttribute('data-transitioning', 'false');
    }, 300);
  }

  public reset(): void {
    // Reset to level 0
    const allLevels = this.nav.querySelectorAll<HTMLElement>('.mobile-menu-level');
    allLevels.forEach(level => {
      level.setAttribute('data-active', 'false');
      level.removeAttribute('data-direction');
    });

    const level0 = this.nav.querySelector<HTMLElement>('.mobile-menu-level[data-level="0"]');
    if (level0) {
      level0.setAttribute('data-active', 'true');
    }

    this.currentLevel = 0;
    this.levelHistory = [0];
    this.nav.setAttribute('data-current-level', '0');
  }
}

/**
 * Mobile Menu V2 Positioner Web Component
 * Dynamically calculates header height and positions the mobile menu accordingly
 * Only targets mobile-menu-v2.liquid
 */

class MobileMenuV2Positioner extends HTMLElement {
  private readonly headerV2Selector: string = '.header-v2, .header.header-v2';
  private readonly headerFallbackSelector: string = 'header, .header, [role="banner"]';
  private readonly announcementSelector: string = '.announcement-bar, .announsment, [data-announcement]';
  private readonly menuOverlaySelector: string = '.mobile-menu-overlay[data-version="v2"]';
  private readonly menuPanelSelector: string = '.mobile-menu-overlay[data-version="v2"] + .mobile-menu-panel[data-version="v2"]';
  private resizeObserver: ResizeObserver | null = null;
  private mutationObserver: MutationObserver | null = null;
  private scrollTimeout: number | null = null;
  private navigationController: MobileMenuV2Navigation | null = null;

  constructor() {
    super();
  }

  connectedCallback(): void {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      this.init();
    }
  }

  private init(): void {
    this.calculateAndSetPosition();
    this.setupObservers();
    this.initializeNavigation();
    
    // Recalculate on window resize (handles browser bar show/hide)
    window.addEventListener('resize', this.debounce(() => {
      this.calculateAndSetPosition();
    }, 150));

    // Recalculate on orientation change (mobile)
    window.addEventListener('orientationchange', () => {
      setTimeout(() => {
        this.calculateAndSetPosition();
      }, 200); // Small delay to ensure orientation change is complete
    });

    // Recalculate on scroll (for mobile browser bars that hide on scroll)
    // Use throttle for performance
    window.addEventListener('scroll', () => {
      if (this.scrollTimeout) return;
      this.scrollTimeout = window.setTimeout(() => {
        this.calculateAndSetPosition();
        this.scrollTimeout = null;
      }, 100);
    }, { passive: true });

    // Recalculate when menu opens (in case header changed)
    this.observeMenuToggle();
  }

  private initializeNavigation(): void {
    const nav = document.querySelector<HTMLElement>('.mobile-menu-nav');
    if (nav) {
      this.navigationController = new MobileMenuV2Navigation(nav);
    }
  }

  private calculateAndSetPosition(): void {
    // Prioritize header-v2, fallback to generic header selector
    let header = document.querySelector<HTMLElement>(this.headerV2Selector);
    if (!header) {
      header = document.querySelector<HTMLElement>(this.headerFallbackSelector);
    }
    const announcement = document.querySelector<HTMLElement>(this.announcementSelector);
    
    let totalHeight: number = 0;

    // Calculate header height
    if (header) {
      const headerRect = header.getBoundingClientRect();
      totalHeight += headerRect.height;
    }

    // Add announcement bar height if it exists and is visible
    if (announcement) {
      const announcementRect = announcement.getBoundingClientRect();
      const announcementStyles = window.getComputedStyle(announcement);
      
      if (announcementStyles.display !== 'none' && announcementStyles.visibility !== 'hidden') {
        totalHeight += announcementRect.height;
      }
    }

    // Calculate actual viewport height (handles mobile browser bars)
    // window.innerHeight changes when address bar shows/hides
    const actualViewportHeight: number = window.innerHeight;
    const menuHeight: number = actualViewportHeight - totalHeight;

    // Set CSS custom properties for use in styles
    document.documentElement.style.setProperty('--mobile-menu-v2-offset', `${totalHeight}px`);
    document.documentElement.style.setProperty('--mobile-menu-v2-height', `${menuHeight}px`);
    document.documentElement.style.setProperty('--viewport-height-mobile', `${actualViewportHeight}px`);
    
    // Also set data attribute for potential use in other scripts
    document.documentElement.setAttribute('data-menu-v2-offset', totalHeight.toString());

  }

  private setupObservers(): void {
    // Use ResizeObserver to watch header size changes
    // Prioritize header-v2, fallback to generic header selector
    let header = document.querySelector<HTMLElement>(this.headerV2Selector);
    if (!header) {
      header = document.querySelector<HTMLElement>(this.headerFallbackSelector);
    }
    const announcement = document.querySelector<HTMLElement>(this.announcementSelector);

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => {
        this.calculateAndSetPosition();
      });

      if (header) {
        this.resizeObserver.observe(header);
      }
      if (announcement) {
        this.resizeObserver.observe(announcement);
      }
    }

    // Watch for DOM changes (in case announcement bar is added/removed dynamically)
    if ('MutationObserver' in window) {
      this.mutationObserver = new MutationObserver(() => {
        this.calculateAndSetPosition();
      });

      const observerConfig: MutationObserverInit = {
        attributes: true,
        attributeFilter: ['style', 'class'],
        subtree: true
      };

      if (header) {
        this.mutationObserver.observe(header, observerConfig);
      }
      if (announcement) {
        this.mutationObserver.observe(announcement, observerConfig);
      }
    }
  }

  private observeMenuToggle(): void {
    const menuOverlay = document.querySelector<HTMLElement>(this.menuOverlaySelector);
    
    if (!menuOverlay) return;

    // Use MutationObserver to watch for class changes on menu
    const menuObserver = new MutationObserver(() => {
      if (menuOverlay.classList.contains('active')) {
        // Menu opened - recalculate in case anything changed
        this.calculateAndSetPosition();
        // Hide chat widget
        this.toggleChatWidget(false);
      } else {
        // Menu closed - reset navigation to level 0
        if (this.navigationController) {
          this.navigationController.reset();
        }
        // Show chat widget
        this.toggleChatWidget(true);
      }
    });

    menuObserver.observe(menuOverlay, {
      attributes: true,
      attributeFilter: ['class']
    });
  }

  private toggleChatWidget(visible: boolean): void {
    const chatWidget = document.querySelector<HTMLElement>('#shopify-chat');
    if (chatWidget) {
      chatWidget.style.display = visible ? '' : 'none';
    }
  }

  private debounce<T extends (...args: any[]) => void>(
    func: T, 
    wait: number
  ): (...args: Parameters<T>) => void {
    let timeout: number | undefined;
    return function(this: any, ...args: Parameters<T>): void {
      const later = (): void => {
        clearTimeout(timeout);
        func.apply(this, args);
      };
      clearTimeout(timeout);
      timeout = window.setTimeout(later, wait);
    };
  }

  disconnectedCallback(): void {
    // Cleanup observers
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
  }
}

// Export for module system - registered in src/script/index.ts
export default MobileMenuV2Positioner;

